import type { AdvancedFilter, Filter, FilterConditions, FlattenedFilter } from '@enterprise/types';

/**
 * Flattens a nested structure of filter conditions and filters into a flat array of `FlattenedFilter` objects.
 *
 * This function traverses the provided filter conditions tree (or array of trees), assigning unique group IDs
 * and preserving parent-child relationships via `parentGroupId`. It supports various filter types including
 * boolean, date, dateString, number, and text.
 *
 * @param root - The root filter condition(s) to flatten.
 * Can be a single `Filter`, `FilterConditions`, or an array of them.
 * @param parentGroupId - The group ID of the parent node, or `null` if at the root level. Defaults to `null`.
 * @param startId - The starting value for group IDs. Defaults to `1`.
 * @returns An array of `FlattenedFilter` objects representing the flattened filter conditions tree.
 * @throws Error if an unsupported filter type is encountered.
 */
export const flattenFilterConditions = (
  root: AdvancedFilter | AdvancedFilter[],
  parentGroupId: null | number = null,
  startId = 1
) => {
  const rows: FlattenedFilter[] = [];
  let nextId = startId;

  function traverse(node: Filter | FilterConditions, parentId: null | number) {
    if ('conditions' in node) {
      const { conditions, field, operator, type } = node;
      const groupId = nextId++;

      rows.push({
        field,
        groupId,
        operator,
        parentGroupId: parentId,
        type,
      });

      for (const child of conditions) {
        traverse(child, groupId);
      }

      return;
    }

    const { field, operation, type } = node;
    const groupId = nextId++;

    switch (type) {
      case 'boolean':
        rows.push({
          field,
          groupId,
          operation,
          parentGroupId: parentId,
          type,
        });
        break;

      case 'date':

      case 'dateString':

      case 'number': {
        const { value, valueTo } = node;

        rows.push({
          field,
          groupId,
          operation,
          parentGroupId: parentId,
          type,
          value,
          valueTo,
        });
        break;
      }

      case 'text': {
        const { value } = node;

        rows.push({
          field,
          groupId,
          operation,
          parentGroupId: parentId,
          type,
          value,
        });
        break;
      }

      default:
        throw new Error('Unsupported filter type');
    }
  }

  if (Array.isArray(root)) {
    for (const item of root) {
      traverse(item, parentGroupId);
    }
  } else {
    traverse(root, parentGroupId);
  }

  return rows;
};
